import React from "react";
import {
    BarChart3,
    CalendarDays,
    CheckCircle2,
    Clock3,
    Flag,
    GraduationCap,
    LineChart,
    Medal,
    Target,
    TrendingUp,
} from "lucide-react";

const kpis = [
    { label: "Progression globale", value: "68%", hint: "+12% ce mois", icon: TrendingUp, tone: "blue" },
    { label: "Campagnes actives", value: "2", hint: "2026 en cours", icon: Flag, tone: "coral" },
    { label: "Évaluations terminées", value: "9", hint: "sur 11 ciblées", icon: CheckCircle2, tone: "green" },
    { label: "Validations en attente", value: "9", hint: "à traiter", icon: Clock3, tone: "amber" },
];

const steps = [
    { label: "Objectifs N-1", status: "Terminé", progress: 100, icon: Target },
    { label: "Compétences génériques", status: "Validé", progress: 100, icon: Medal },
    { label: "Compétences spécifiques", status: "En cours", progress: 72, icon: BarChart3 },
    { label: "Plan de développement", status: "Planifié", progress: 45, icon: GraduationCap },
    { label: "Objectifs N", status: "À réviser", progress: 38, icon: LineChart },
];

const actions = [
    "Finaliser les compétences spécifiques",
    "Valider les auto-évaluations en attente",
    "Planifier les entretiens annuels",
    "Suivre les demandes de révision d'objectifs",
];

function ToneIcon({ icon: Icon, tone }) {
    return (
        <span className={`tdb-icon tdb-icon--${tone}`}>
            <Icon size={20} />
        </span>
    );
}

export default function TDB() {
    return (
        <main className="tdb-page">
            <style>{`
                .tdb-page {
                    min-height: 100vh;
                    padding: 24px;
                    background: #f7f9fc;
                    color: #111827;
                    font-family: Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
                }

                .tdb-shell {
                    display: grid;
                    gap: 18px;
                    max-width: 1440px;
                    margin: 0 auto;
                }

                .tdb-hero {
                    display: grid;
                    grid-template-columns: minmax(0, 1.5fr) minmax(320px, .8fr);
                    gap: 18px;
                    align-items: stretch;
                }

                .tdb-hero-main,
                .tdb-card,
                .tdb-panel {
                    background: #fff;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    box-shadow: 0 8px 24px rgba(32, 67, 133, .06);
                }

                .tdb-hero-main {
                    position: relative;
                    overflow: hidden;
                    padding: 28px;
                    min-height: 245px;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                }

                .tdb-hero-main::after {
                    content: "";
                    position: absolute;
                    inset: auto -80px -130px auto;
                    width: 340px;
                    height: 340px;
                    border-radius: 50%;
                    background: radial-gradient(circle, rgba(255, 77, 79, .18), rgba(32, 67, 133, .04) 58%, transparent 70%);
                    pointer-events: none;
                }

                .tdb-eyebrow {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    width: fit-content;
                    padding: 6px 10px;
                    border-radius: 999px;
                    background: #e8edf7;
                    color: #204385;
                    font-size: 12px;
                    font-weight: 800;
                }

                .tdb-title {
                    margin: 18px 0 8px;
                    color: #204385;
                    font-size: 32px;
                    line-height: 1.12;
                    font-weight: 850;
                    letter-spacing: 0;
                }

                .tdb-subtitle {
                    max-width: 740px;
                    margin: 0;
                    color: #5b6472;
                    font-size: 14px;
                    line-height: 1.6;
                }

                .tdb-hero-bottom {
                    display: grid;
                    grid-template-columns: repeat(3, minmax(0, 1fr));
                    gap: 12px;
                    margin-top: 26px;
                    position: relative;
                    z-index: 1;
                }

                .tdb-mini {
                    padding: 13px 14px;
                    border: 1px solid #e8edf7;
                    border-radius: 10px;
                    background: #fbfdff;
                }

                .tdb-mini span {
                    display: block;
                    color: #6b7280;
                    font-size: 11px;
                    font-weight: 700;
                    text-transform: uppercase;
                }

                .tdb-mini strong {
                    display: block;
                    margin-top: 6px;
                    color: #204385;
                    font-size: 18px;
                }

                .tdb-score-card {
                    padding: 22px;
                    display: grid;
                    align-content: center;
                    justify-items: center;
                    text-align: center;
                }

                .tdb-ring {
                    width: 150px;
                    aspect-ratio: 1;
                    border-radius: 50%;
                    display: grid;
                    place-items: center;
                    background: conic-gradient(#ff4d4f 0 68%, #e8edf7 68% 100%);
                    box-shadow: inset 0 0 0 1px #e5e7eb;
                }

                .tdb-ring-inner {
                    width: 112px;
                    aspect-ratio: 1;
                    border-radius: 50%;
                    display: grid;
                    place-items: center;
                    background: #fff;
                    color: #204385;
                    font-size: 30px;
                    font-weight: 900;
                }

                .tdb-score-card h3 {
                    margin: 18px 0 6px;
                    color: #204385;
                    font-size: 17px;
                }

                .tdb-score-card p {
                    margin: 0;
                    color: #6b7280;
                    font-size: 13px;
                }

                .tdb-kpis {
                    display: grid;
                    grid-template-columns: repeat(4, minmax(0, 1fr));
                    gap: 14px;
                }

                .tdb-card {
                    padding: 18px;
                    display: grid;
                    gap: 12px;
                }

                .tdb-card-top {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                }

                .tdb-icon {
                    width: 42px;
                    height: 42px;
                    border-radius: 12px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    color: #204385;
                    background: #e8edf7;
                }

                .tdb-icon--coral { color: #ff4d4f; background: #fff1f2; }
                .tdb-icon--green { color: #059669; background: #d1fae5; }
                .tdb-icon--amber { color: #b45309; background: #fef3c7; }
                .tdb-icon--blue { color: #204385; background: #e8edf7; }

                .tdb-kpi-value {
                    color: #111827;
                    font-size: 28px;
                    font-weight: 900;
                    line-height: 1;
                }

                .tdb-kpi-label {
                    margin: 0;
                    color: #204385;
                    font-size: 13px;
                    font-weight: 800;
                }

                .tdb-kpi-hint {
                    color: #6b7280;
                    font-size: 12px;
                    font-weight: 600;
                }

                .tdb-grid {
                    display: grid;
                    grid-template-columns: minmax(0, 1.25fr) minmax(360px, .75fr);
                    gap: 18px;
                }

                .tdb-panel {
                    padding: 20px;
                    min-width: 0;
                }

                .tdb-panel-head {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    margin-bottom: 16px;
                }

                .tdb-panel-title {
                    margin: 0;
                    color: #204385;
                    font-size: 17px;
                    font-weight: 850;
                }

                .tdb-pill {
                    padding: 5px 10px;
                    border-radius: 999px;
                    background: #f3f6fb;
                    color: #204385;
                    font-size: 11px;
                    font-weight: 800;
                    white-space: nowrap;
                }

                .tdb-steps {
                    display: grid;
                    gap: 12px;
                }

                .tdb-step {
                    display: grid;
                    grid-template-columns: auto minmax(0, 1fr) auto;
                    align-items: center;
                    gap: 12px;
                    padding: 13px;
                    border: 1px solid #edf0f5;
                    border-radius: 10px;
                    background: #fbfdff;
                }

                .tdb-step h4 {
                    margin: 0;
                    color: #111827;
                    font-size: 13px;
                    font-weight: 800;
                }

                .tdb-step p {
                    margin: 4px 0 0;
                    color: #6b7280;
                    font-size: 12px;
                    line-height: 1.45;
                }

                .tdb-progress {
                    height: 7px;
                    border-radius: 999px;
                    background: #edf0f5;
                    overflow: hidden;
                    margin-top: 9px;
                }

                .tdb-progress span {
                    display: block;
                    height: 100%;
                    border-radius: inherit;
                    background: linear-gradient(90deg, #204385, #ff4d4f);
                }

                .tdb-status {
                    padding: 5px 9px;
                    border-radius: 999px;
                    color: #204385;
                    background: #e8edf7;
                    font-size: 11px;
                    font-weight: 800;
                    white-space: nowrap;
                }

                .tdb-actions {
                    display: grid;
                    gap: 10px;
                }

                .tdb-action {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 12px;
                    border: 1px solid #edf0f5;
                    border-radius: 10px;
                    background: #fff;
                    color: #374151;
                    font-size: 13px;
                    font-weight: 700;
                }

                .tdb-action::before {
                    content: "";
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background: #ff4d4f;
                    flex: 0 0 auto;
                }

                @media (max-width: 1180px) {
                    .tdb-hero,
                    .tdb-grid {
                        grid-template-columns: 1fr;
                    }

                    .tdb-kpis {
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                    }
                }

                @media (max-width: 760px) {
                    .tdb-page {
                        padding: 14px;
                    }

                    .tdb-hero-main {
                        padding: 20px;
                    }

                    .tdb-title {
                        font-size: 24px;
                    }

                    .tdb-hero-bottom,
                    .tdb-kpis {
                        grid-template-columns: 1fr;
                    }

                    .tdb-step {
                        grid-template-columns: auto minmax(0, 1fr);
                    }

                    .tdb-status {
                        grid-column: 2;
                        text-align: left;
                        width: fit-content;
                    }
                }
            `}</style>

            <div className="tdb-shell">
                <section className="tdb-hero">
                    <div className="tdb-hero-main">
                        <div>
                            <span className="tdb-eyebrow">
                                <CalendarDays size={15} />
                                Campagne Performance 2026
                            </span>
                            <h1 className="tdb-title">Tableau de bord performance</h1>
                            <p className="tdb-subtitle">
                                Vue générale du cycle d'évaluation annuelle : progression, validations,
                                objectifs, compétences et plan de développement.
                            </p>
                        </div>

                        <div className="tdb-hero-bottom">
                            <div className="tdb-mini">
                                <span>Période</span>
                                <strong>Mai - Déc. 2026</strong>
                            </div>
                            <div className="tdb-mini">
                                <span>Statut campagne</span>
                                <strong>En cours</strong>
                            </div>
                            <div className="tdb-mini">
                                <span>Prochaine étape</span>
                                <strong>Validation manager</strong>
                            </div>
                        </div>
                    </div>

                    <aside className="tdb-score-card tdb-panel">
                        <div className="tdb-ring">
                            <div className="tdb-ring-inner">68%</div>
                        </div>
                        <h3>Avancement moyen</h3>
                        <p>3 étapes bien avancées sur 5 dans le cycle courant.</p>
                    </aside>
                </section>

                <section className="tdb-kpis">
                    {kpis.map((item) => (
                        <article className="tdb-card" key={item.label}>
                            <div className="tdb-card-top">
                                <ToneIcon icon={item.icon} tone={item.tone} />
                                <span className="tdb-kpi-value">{item.value}</span>
                            </div>
                            <div>
                                <p className="tdb-kpi-label">{item.label}</p>
                                <span className="tdb-kpi-hint">{item.hint}</span>
                            </div>
                        </article>
                    ))}
                </section>

                <section className="tdb-grid">
                    <div className="tdb-panel">
                        <div className="tdb-panel-head">
                            <h2 className="tdb-panel-title">Suivi des étapes</h2>
                            <span className="tdb-pill">Cycle annuel</span>
                        </div>

                        <div className="tdb-steps">
                            {steps.map((step) => (
                                <article className="tdb-step" key={step.label}>
                                    <ToneIcon icon={step.icon} tone={step.progress === 100 ? "green" : "blue"} />
                                    <div>
                                        <h4>{step.label}</h4>
                                        <p>Progression estimée de l'étape dans la campagne active.</p>
                                        <div className="tdb-progress">
                                            <span style={{ width: `${step.progress}%` }} />
                                        </div>
                                    </div>
                                    <span className="tdb-status">{step.status}</span>
                                </article>
                            ))}
                        </div>
                    </div>

                    <div className="tdb-panel">
                        <div className="tdb-panel-head">
                            <h2 className="tdb-panel-title">Actions prioritaires</h2>
                            <span className="tdb-pill">Cette semaine</span>
                        </div>

                        <div className="tdb-actions">
                            {actions.map((action) => (
                                <div className="tdb-action" key={action}>{action}</div>
                            ))}
                        </div>
                    </div>
                </section>

            </div>
        </main>
    );
}
